import JitsiMeetJS from '../base/lib-jitsi-meet';
import { getParticipantById, getVirtualScreenshareParticipantOwnerId, isScreenShareParticipant } from '../base/participants/functions';
import { getLocalDesktopTrack, getVideoTrackByParticipant } from '../base/tracks/functions';
import { enableReceiver, stopReceiver } from './actions';
import { EVENTS, REMOTE_CONTROL_MENU_STATES, REMOTE_CONTROL_MESSAGE_NAME } from './constants';
import { keyboardEventToKey } from './keycodes';
import logger from './logger';
/**
 * Checks if the remote control is enabled.
 *
 * @param {*} state - The redux state.
 * @returns {boolean} - True if the remote control is enabled and false otherwise.
 */
export function isRemoteControlEnabled(state) {
    return !state['features/base/config'].disableRemoteControl && JitsiMeetJS.isDesktopSharingEnabled();
}
/**
 * Sends remote control message to other participant through data channel.
 *
 * @param {JitsiConference} conference - The JitsiConference object.
 * @param {string} to - The participant who will receive the event.
 * @param {RemoteControlEvent} event - The remote control event.
 * @returns {boolean} - True if the message was sent successfully and false otherwise.
 */
export function sendRemoteControlEndpointMessage(conference, to, event) {
    if (!to) {
        logger.warn('Remote control: Skip sending remote control event. Params:', to);
        return false;
    }
    try {
        conference?.sendEndpointMessage(to, {
            name: REMOTE_CONTROL_MESSAGE_NAME,
            ...event
        });
        return true;
    }
    catch (error) {
        logger.error('Failed to send EndpointMessage via the datachannels', error);
        return false;
    }
}
/**
* Handles remote control events from the external app. Currently only
* events with type EVENTS.supported and EVENTS.stop are
* supported.
*
* @param {RemoteControlEvent} event - The remote control event.
* @param {Store} store - The redux store.
* @returns {void}
*/
export function onRemoteControlAPIEvent(event, { getState, dispatch }) {
    switch (event.type) {
        case EVENTS.supported:
            logger.log('Remote Control supported.');
            if (isRemoteControlEnabled(getState())) {
                dispatch(enableReceiver());
            }
            else {
                logger.log('Remote Control disabled.');
            }
            break;
        case EVENTS.stop: {
            dispatch(stopReceiver());
            break;
        }
    }
}
/**
 * Works out what the "request control" menu item should say for a participant,
 * or undefined when it should not be offered at all.
 *
 * @param {IReduxState} state - The redux state.
 * @param {string} participantId - The participant the menu is about.
 * @returns {number|undefined} One of REMOTE_CONTROL_MENU_STATES.
 */
export function getRemoteControlMenuState(state, participantId) {
    if (!participantId) {
        return undefined;
    }
    const participant = getParticipantById(state, participantId);
    if (!participant?.supportsRemoteControl) {
        return undefined;
    }
    const { active, controller } = state['features/remote-control'];
    const { controlled, requestedParticipant } = controller;
    const inSessionElsewhere = participant.remoteControlSessionStatus ?? false;
    const activeParticipant = requestedParticipant || controlled;
    // Offer it when nothing is going on, or when what is going on is this
    // participant. Offering it during somebody else's session would only
    // produce a request that cannot be honoured.
    if ((active || inSessionElsewhere) && activeParticipant !== participantId) {
        return undefined;
    }
    if (requestedParticipant === participantId) {
        return REMOTE_CONTROL_MENU_STATES.REQUESTING;
    }
    if (controlled === participantId) {
        return REMOTE_CONTROL_MENU_STATES.STARTED;
    }
    return REMOTE_CONTROL_MENU_STATES.NOT_STARTED;
}
/**
 * Returns the participant whose shared screen is on stage and could be taken
 * control of, if there is one.
 *
 * This is what puts an offer to take control next to the thing it would control.
 * Upstream buried the only way to ask inside a thumbnail's overflow menu, which
 * meant the feature was invisible unless you already knew it existed.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {string|undefined} The participant's id.
 */
export function getControllableParticipantOnStage(state) {
    if (!isRemoteControlEnabled(state)) {
        return undefined;
    }
    const { participantId } = state['features/large-video'];
    if (!participantId) {
        return undefined;
    }
    const onStage = getParticipantById(state, participantId);
    // With multistream a shared screen is its own participant, and the person
    // to ask is its owner. Without it the screen arrives as the participant's
    // own video, and the person to ask is them.
    const ownerId = isScreenShareParticipant(onStage)
        ? getVirtualScreenshareParticipantOwnerId(participantId)
        : participantId;
    const owner = getParticipantById(state, ownerId);
    if (!owner || owner.local || !owner.supportsRemoteControl) {
        return undefined;
    }
    if (!isScreenShareParticipant(onStage) && !isSharingDesktop(state, participantId)) {
        return undefined;
    }
    return ownerId;
}
/**
 * Reports whether the participant's video on stage is a shared screen rather
 * than a camera. Only a screen can be controlled: there is nothing to click on
 * in a picture of somebody's face.
 *
 * @param {IReduxState} state - The redux state.
 * @param {string} participantId - The participant on stage.
 * @returns {boolean}
 */
function isSharingDesktop(state, participantId) {
    const participant = getParticipantById(state, participantId);
    const track = getVideoTrackByParticipant(state, participant);
    return track?.videoType === 'desktop';
}
/**
 * Returns the video element showing the screen being controlled.
 *
 * @returns {HTMLVideoElement|null}
 */
export function getRemoteControlVideoElement() {
    return document.getElementById('largeVideo');
}
/**
 * Converts a pointer position on screen into a position on the shared desktop,
 * as a fraction of it.
 *
 * Upstream measured against the large-video *wrapper*, but the video is
 * letterboxed inside that wrapper whenever the shared screen and the browser
 * window disagree about their shape — which is nearly always. The remote
 * pointer then drifted further from the real one the more the two differed.
 * Jitsi sizes and positions the video element explicitly, so its own rectangle
 * is the honest reference under every VIDEO_LAYOUT_FIT mode, including the
 * cropping ones where the video overflows the wrapper.
 *
 * @param {number} clientX - The pointer's horizontal viewport position.
 * @param {number} clientY - The pointer's vertical viewport position.
 * @returns {Object|undefined} The position as fractions, or undefined when
 * there is no video to measure against.
 */
export function toSharedScreenPosition(clientX, clientY) {
    const video = getRemoteControlVideoElement();
    if (!video) {
        return undefined;
    }
    const rect = video.getBoundingClientRect();
    if (!rect.width || !rect.height) {
        return undefined;
    }
    return {
        x: clamp((clientX - rect.left) / rect.width),
        y: clamp((clientY - rect.top) / rect.height)
    };
}
/**
 * Keeps a fraction inside the shared screen.
 *
 * @param {number} value - The fraction.
 * @returns {number}
 */
function clamp(value) {
    return Math.min(1, Math.max(0, value));
}
/**
 * Returns the dimensions of the screen currently being shared.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {Object}
 */
export function getSharedDisplaySize(state) {
    const settings = getDesktopTrackSettings(state);
    return {
        height: settings?.height ?? 0,
        width: settings?.width ?? 0
    };
}
/**
 * Reports whether the local participant is sharing a whole screen, as opposed
 * to one window or one browser tab.
 *
 * Only a whole screen can be controlled: a window's position on the desktop is
 * not in the captured video, so a click at the middle of the video cannot be
 * turned into a point on the desktop.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isWholeScreenShared(state) {
    const settings = getDesktopTrackSettings(state);
    const surface = settings?.displaySurface;
    if (surface) {
        return surface === 'monitor';
    }
    // Some browsers do not report displaySurface at all. A capture whose
    // dimensions are exactly those of an attached display is a whole screen,
    // which answers the question without the browser's help.
    return Boolean(findSharedDisplayBySize(state, 0));
}
/**
 * Works out which physical display the browser is sharing.
 *
 * The browser deliberately will not say, so this is deduction: an exact
 * size match settles it, and otherwise the shape of the capture narrows it
 * down. When neither is conclusive the primary display is the best guess, and
 * the person granting control can correct it before they say yes.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {IAgentDisplay|undefined}
 */
export function findSharedDisplay(state) {
    const { chosenDisplayId, hello } = getAgentSelection(state);
    const displays = hello?.displays ?? [];
    if (!displays.length) {
        return undefined;
    }
    if (chosenDisplayId) {
        const chosen = displays.find(display => display.id === chosenDisplayId);
        if (chosen) {
            return chosen;
        }
    }
    if (displays.length === 1) {
        return displays[0];
    }
    return findSharedDisplayBySize(state, 0.01)
        ?? displays.find(display => display.primary)
        ?? displays[0];
}
/**
 * Finds the one display whose geometry matches the capture.
 *
 * @param {IReduxState} state - The redux state.
 * @param {number} tolerance - How far the aspect ratios may differ, as a
 * fraction. Zero demands an exact pixel match.
 * @returns {IAgentDisplay|undefined} The single match, or undefined when there
 * is none or more than one.
 */
function findSharedDisplayBySize(state, tolerance) {
    const { height, width } = getSharedDisplaySize(state);
    const displays = getAgentSelection(state).hello?.displays ?? [];
    if (!width || !height) {
        return undefined;
    }
    const exact = displays.filter(display => display.width === width && display.height === height);
    if (exact.length === 1) {
        return exact[0];
    }
    if (!tolerance) {
        return undefined;
    }
    // A capture is often scaled down from the display it came from, so shape
    // survives where size does not.
    const captured = width / height;
    const similar = displays.filter(display => Math.abs((display.width / display.height) - captured) / captured <= tolerance);
    return similar.length === 1 ? similar[0] : undefined;
}
/**
 * Returns the agent's report and the display the user picked, if any.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {Object}
 */
function getAgentSelection(state) {
    const { hello } = state['features/remote-control'].agent;
    return {
        chosenDisplayId: state['features/remote-control'].agent.chosenDisplayId,
        hello
    };
}
/**
 * Returns the settings of the local desktop track.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {MediaTrackSettings|undefined}
 */
function getDesktopTrackSettings(state) {
    const track = getLocalDesktopTrack(state['features/base/tracks']);
    try {
        return track?.jitsiTrack?.getTrack?.()?.getSettings?.();
    }
    catch (error) {
        logger.warn('Could not read the shared screen settings', error);
        return undefined;
    }
}
/**
 * Extract the keyboard key from the keyboard event.
 *
 * @param {KeyboardEvent} event - The event.
 * @returns {KEYS} The key that is pressed or undefined.
 */
export function getKey(event) {
    return keyboardEventToKey(event);
}
/**
 * Extract the modifiers from the keyboard event.
 *
 * @param {KeyboardEvent} event - The event.
 * @returns {Array} With possible values: "shift", "control", "alt", "command".
 */
export function getModifiers(event) {
    const modifiers = [];
    if (event.shiftKey) {
        modifiers.push('shift');
    }
    if (event.ctrlKey) {
        modifiers.push('control');
    }
    if (event.altKey) {
        modifiers.push('alt');
    }
    if (event.metaKey) {
        modifiers.push('command');
    }
    return modifiers;
}
